import { API_BASE_URL } from '@api/config';

/**
 * Empty in dev, where the Vite proxy already makes the API same-origin, and a
 * separate host (`https://os-api…`) in the deployed environments. Resolving
 * against the page origin collapses both cases to one origin to compare with.
 */
const API_ORIGIN = ((): string | null => {
  try {
    return new URL(API_BASE_URL, globalThis.location.origin).origin;
  } catch {
    return null;
  }
})();

/**
 * Whether a URL may be fetched with the user's Bearer token attached. Only the
 * page's own origin and the API host qualify — sending the token anywhere else,
 * a presigned S3 host included, would hand it to a party that has no business
 * holding it.
 */
export const isTrustedApiUrl = (rawUrl: string): boolean => {
  try {
    const { origin } = new URL(rawUrl, globalThis.location.origin);
    return origin === globalThis.location.origin || origin === API_ORIGIN;
  } catch {
    return false;
  }
};

const API_PATH_PREFIX = '/api/';

/** Static assets share the page origin, so the origin alone cannot tell them from the API. */
export const isOwnApiUrl = (rawUrl: string): boolean => {
  if (!isTrustedApiUrl(rawUrl)) return false;
  return new URL(rawUrl, globalThis.location.origin).pathname.startsWith(API_PATH_PREFIX);
};

/**
 * The client applies its `baseURL` only to relative URLs, and once deployed the
 * page host answers an API path with the SPA's index.html.
 */
export const apiRequestUrl = (rawUrl: string): string => {
  try {
    const url = new URL(rawUrl, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin || !url.pathname.startsWith(API_PATH_PREFIX)) {
      return rawUrl;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return rawUrl;
  }
};
